import { useEffect, Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import ProtectedRoute from './components/ProtectedRoute';
import { Box, useToast, Spinner, Flex } from '@chakra-ui/react';
import TopNavbar from './components/TopNavbar';
import BottomNavbar from './components/BottomNavbar';
import ScrollManager from './components/ScrollManager';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { setAuthAndNavigationFunctions, setToastFunction } from './utils/api';
import useDocumentTitle from './hooks/useDocumentTitle';

// Lazy load components
const LandingPage = lazy(() => import('./components/LandingPage'));
const PopupModal = lazy(() => import('./components/PopupModal'));
const SellerDashboard = lazy(() => import('./components/SellerDashboard'));
const BuyerDashboard = lazy(() => import('./components/BuyerDashboard'));
const OrderPage = lazy(() => import('./components/OrderPage'));
const ProductListingPage = lazy(() => import('./components/ProductListingPage'));
const Message = lazy(() => import('./components/Message'));
const Notification = lazy(() => import('./components/Notification'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const ProductPage = lazy(() => import('./components/ProductPage'));
const ProductDetailsPage = lazy(() => import('./components/ProductDetailsPage'));
const BasketPage = lazy(() => import('./components/BasketPage'));
const ChatPage = lazy(() => import('./components/ChatPage'));
const AddProductPage = lazy(() => import('./components/AddProductPage'));
const SellerSignupPage = lazy(() => import('./components/SellerSignupPage'));
const OrderDeliveryManagement = lazy(() => import('./components/OrderDeliveryManagement'));
const AgentRegistrationPage = lazy(() => import('./components/AgentRegistrationPage'));
const AgentDashboard = lazy(() => import('./components/AgentDashboard'));
const AgentReferralPage = lazy(() => import('./components/AgentReferralPage'));
const AgentMessagesPage = lazy(() => import('./pages/AgentMessagesPage'));
const LogisticsTaskList = lazy(() => import('./components/LogisticsTaskList'));
const Wallet = lazy(() => import('./components/Wallet'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PiAuthCallbackPage = lazy(() => import('./pages/PiAuthCallbackPage'));
const AddressBookPage = lazy(() => import('./components/AddressBookPage'));
const RefundsHistoryPage = lazy(() => import('./pages/RefundsHistoryPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const SellersPage = lazy(() => import('./pages/SellersPage'));
const PulseAgentsPage = lazy(() => import('./pages/PulseAgentsPage'));
const SellerProfile = lazy(() => import('./components/SellerProfile'));
const CatalogueSearchPage = lazy(() => import('./components/CatalogueSearchPage'));
const SellerApplicationStatusPage = lazy(() => import('./components/SellerApplicationStatusPage'));
const AgentApplicationStatusPage = lazy(() => import('./components/AgentApplicationStatusPage'));


const pagesWithNavbars = ['seller-dashboard', 'buyer-dashboard', 'product-listing', 'messages', 'notifications', 'chat', 'seller-profile', 'catalogue-search', 'profile/addresses'];

function Main() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const toast = useToast();

  useEffect(() => {
    setAuthAndNavigationFunctions(logout);
    setToastFunction(toast);
  }, [logout, toast]);

  const getTitle = (pathname) => {
    const titleMap = {
      '/': 'Home - Nature\'s Pulse',
      '/home': 'Welcome - Nature\'s Pulse',
      '/login': 'Login - Nature\'s Pulse',
      '/seller-dashboard': 'Seller Dashboard - Nature\'s Pulse',
      '/seller-profile': 'Seller Profile - Nature\'s Pulse',
      '/seller/catalogue-search': 'Catalogue Search - Nature\'s Pulse',
      '/add-product': 'Add Product - Nature\'s Pulse',
      '/buyer-dashboard': 'Buyer Dashboard - Nature\'s Pulse',
      '/product-listing': 'Products - Nature\'s Pulse',
      '/messages': 'Messages - Nature\'s Pulse',
      '/notifications': 'Notifications - Nature\'s Pulse',
      '/seller-signup': 'Seller Signup - Nature\'s Pulse',
      '/basket': 'Your Basket - Nature\'s Pulse',
      '/order': 'Order Details - Nature\'s Pulse',
      '/order-delivery': 'Order Delivery - Nature\'s Pulse',
      '/chat': 'Chat - Nature\'s Pulse',
      '/agent-registration': 'Agent Registration - Nature\'s Pulse',
      '/agent-dashboard': 'Agent Dashboard - Nature\'s Pulse',
      '/agent-referrals': 'Agent Referrals - Nature\'s Pulse',
      '/agent-messages': 'Agent Messages - Nature\'s Pulse',
      '/logistics-tasks': 'Logistics Tasks - Nature\'s Pulse',
      '/wallet': 'Wallet - Nature\'s Pulse',
      '/refunds-history': 'Refunds History - Nature\'s Pulse',
      '/terms-of-service': 'Terms of Service - Nature\'s Pulse',
      '/privacy-policy': 'Privacy Policy - Nature\'s Pulse',
      '/about-us': 'About Us - Nature\'s Pulse',
      '/sellers': 'Our Sellers - Nature\'s Pulse',
      '/pulse-agents': 'Pulse Agents - Nature\'s Pulse',
      '/seller-application-status': 'Application Status - Nature\'s Pulse',
      '/agent-application-status': 'Application Status - Nature\'s Pulse',
      '/profile/addresses': 'My Address Book - Nature\'s Pulse',
    };

    if (pathname.startsWith('/product-details/') || pathname.startsWith('/seller/products/')) {
      return null;
    }

    return titleMap[pathname] || "Nature's Pulse";
  };

  const title = getTitle(location.pathname);
  useDocumentTitle(title);

  const currentPath = location.pathname.slice(1);
  const shouldDisplayNavbars = pagesWithNavbars.includes(currentPath) || location.pathname === '/';
  const shouldDisplayBottomNavbar = shouldDisplayNavbars;

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        keepPreviousData: true,
        retry: 1,
      }
    }
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollManager />
      <Box className="App" minH="100vh" position="relative">
        {shouldDisplayNavbars && <TopNavbar />}
        <Box pt={shouldDisplayNavbars ? "60px" : "0"} pb={shouldDisplayNavbars ? "60px" : "0"}>
          <Suspense fallback={<Flex justify="center" align="center" height="100vh"><Spinner size="xl" /></Flex>}>
            <Routes>
              <Route path="/" element={<ProductListingPage />} />
              <Route path="/home" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signin/callback" element={<PiAuthCallbackPage />} />
              <Route path="/profile/addresses" element={<ProtectedRoute><AddressBookPage /></ProtectedRoute>} />
              <Route path="/seller-dashboard" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />
              <Route path="/seller-profile" element={<ProtectedRoute><SellerProfile /></ProtectedRoute>} />
              <Route path="/seller/catalogue-search" element={<ProtectedRoute><CatalogueSearchPage /></ProtectedRoute>} />
              <Route path="/add-product" element={<ProtectedRoute><AddProductPage /></ProtectedRoute>} />
              <Route path="/buyer-dashboard" element={<ProtectedRoute><BuyerDashboard /></ProtectedRoute>} />
              <Route path="/product-listing" element={<ProductListingPage />} />
              <Route path="/messages" element={<ProtectedRoute><Message /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notification /></ProtectedRoute>} />
              <Route path="/seller-signup" element={<SellerSignupPage />} />
              <Route path="/seller/products/:productId" element={<ProtectedRoute><ProductDetailsPage /></ProtectedRoute>} />
              <Route path="/product-details/:productId" element={<ProductPage />} />
              <Route path="/basket" element={<ProtectedRoute><BasketPage /></ProtectedRoute>} />
              <Route path="/order/:orderID" element={<ProtectedRoute><OrderPage /></ProtectedRoute>} />
              <Route path="/order-delivery/:orderID" element={<ProtectedRoute><OrderDeliveryManagement /></ProtectedRoute>} />
              <Route path="/chat/:chatId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/agent-registration" element={<ProtectedRoute><AgentRegistrationPage /></ProtectedRoute>} />
              <Route path="/agent-dashboard" element={<ProtectedRoute><AgentDashboard /></ProtectedRoute>} />
              <Route path="/agent-referrals" element={<ProtectedRoute><AgentReferralPage /></ProtectedRoute>} />
              <Route path="/agent-messages" element={<ProtectedRoute><AgentMessagesPage /></ProtectedRoute>} />
              <Route path="/logistics-tasks" element={<ProtectedRoute><LogisticsTaskList /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
              <Route path="/refunds-history" element={<ProtectedRoute><RefundsHistoryPage /></ProtectedRoute>} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/sellers" element={<SellersPage />} />
              <Route path="/pulse-agents" element={<PulseAgentsPage />} />
              <Route path="/seller-application-status" element={<SellerApplicationStatusPage />} />
              <Route path="/agent-application-status" element={<AgentApplicationStatusPage />} />

            </Routes>
          </Suspense>
        </Box>
        {shouldDisplayBottomNavbar && <BottomNavbar />}
        <PopupModal />
      </Box>
    </QueryClientProvider>
  );
}

export default Main;
